var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _class, _temp;

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

/* global $ */
import { GraphQLClient } from "graphql-request";
import mixpanel from "mixpanel-browser";
import nanobus from "nanobus";
import { paymentMutation, sessionIdQuery, savePaymentSessionIdQuery, saveCreditCardMethodMutation, saveACHMethodMutation } from "./queries";

var PORTALONE_SCRIPT_TEST = "https://testportalone.processonepayments.com/SageSureModal/Cdn/PortalOne.js";
var PORTALONE_SCRIPT_PROD = "https://portalone.processonepayments.com/SageSureModal/Cdn/PortalOne.js";

var GENERIC_SCRIPT_TEST = "https://testportalone.processonepayments.com/GenericModal/Cdn/PortalOne.js";
var GENERIC_SCRIPT_PROD = "https://portalone.processonepayments.com/GenericModal/Cdn/PortalOne.js";

var PAYMENT_CONTAINER_ID = "paymentContainer";
var PAYMENT_SCRIPT_ID = "paymentScript";
var PAYMENT_PLUGIN_NAME = "portalOnePayment";
var CARD_CONTAINER_ID = "cardContainer";
var CARD_SCRIPT_ID = "cardScript";
var CARD_PLUGIN_NAME = "portalOneSaveCard";

export var PAYMENT_READY = "payment-ready";
export var PAYMENT_START = "payment-start";
export var PAYMENT_CANCEL = "payment-cancel";
export var PAYMENT_ERROR = "payment-error";
export var PAYMENT_COMPLETE = "payment-complete";
var PAYMENT_COMPLETE_ERROR = "payment-complete-error";
var PAYMENT_NO_PAYLOAD = "payment-no-payload";
export var SAVE_CC_CANCEL = "save-credit-card-cancel";
export var SAVE_CC_COMPLETE = "save-credit-card-complete";
export var SAVE_CC_ERROR = "save-credit-card-error";

export var SAVE_PAYMENT_CC = "CreditCard";
export var SAVE_PAYMENT_ECHECK = "ECheck";

/**
 * Interface for third-party payment service — One, Inc's PortalOne API. This
 * controller will handle injecting the PortalOne script, registering events,
 * and sending a successful payment back to Insight.
 *
 * Make sure jQuery is loaded before initializing this controller. You'll also
 * need polyfills for fetch and promise.
 *
 * @export
 * @class PaymentController
 *
 * @example
 * import { PaymentController, PAYMENT_COMPLETE } from "payment-toolkit";
 *
 * const payControl = new PaymentController({
 *   paymentsEndpoint: "https://.../graphql",
 *   production: true
 * });
 *
 * payControl.addEventListener(PAYMENT_COMPLETE, response => {
 *   // handle successful payment here
 * });
 *
 * payControl.makePayment({
 *   accountBalance: "number",
 *   agencyLocationCode: "string?",
 *   billingCity: "string?",
 *   billingState: "string?",
 *   billingStreet: "string?",
 *   billingZip: "string?",
 *   email: "string",
 *   firstName: "string?",
 *   insightPolicyId: "string?",
 *   lastName: "string?",
 *   minAmountDue: "number",
 *   minCustomPaymentAmount: "number?",
 *   paymentApplicationClient: "string",
 *   phone: "string",
 *   policyId: "string",
 *   quoteId: "string?",
 * })
 */
var PaymentController = (_temp = _class = function () {
  function PaymentController(_ref) {
    var paymentsEndpoint = _ref.paymentsEndpoint,
        production = _ref.production,
        includeSavePayMethod = _ref.includeSavePayMethod;

    _classCallCheck(this, PaymentController);

    this._payment_ready = false;
    this._savecard_ready = !includeSavePayMethod;
    this._client = new GraphQLClient(paymentsEndpoint);
    this._eventbus = nanobus();
    this._injectPortalOneScripts(production, includeSavePayMethod);
    mixpanel.init(production === true ? "175545c2eb28340763653b5961312db3" : "18074e762159409b8f2100512b360051");
  }

  /**
   * Check if controller fully initialized
   *
   * @returns {boolean}
   * @memberof PaymentController
   */


  PaymentController.prototype.isReady = function isReady() {
    return this._payment_ready && this._savecard_ready;
  };

  /**
   * Triggers payment modal after fetching session key.
   *
   * @param {object} payment data passed to PortalOne api
   * @memberof PaymentController
   */


  PaymentController.prototype.makePayment = function makePayment(payment) {
    var _this = this;

    if (!validate(payment)) {
      // eslint-disable-next-line no-console
      console.error("Payment object missing required fields or values of wrong type");
    }

    this._paymentPayload = payment;
    this._initPortalOne(PAYMENT_CONTAINER_ID, PAYMENT_PLUGIN_NAME);

    var _paymentPayload = this._paymentPayload,
        accountBalance = _paymentPayload.accountBalance,
        agencyLocationCode = _paymentPayload.agencyLocationCode,
        billingStreet = _paymentPayload.billingStreet,
        billingZip = _paymentPayload.billingZip,
        insightPolicyId = _paymentPayload.insightPolicyId,
        minAmountDue = _paymentPayload.minAmountDue,
        minCustomPaymentAmount = _paymentPayload.minCustomPaymentAmount,
        paymentApplicationClient = _paymentPayload.paymentApplicationClient,
        firstName = _paymentPayload.firstName,
        lastName = _paymentPayload.lastName,
        policyId = _paymentPayload.policyId;


    mixpanel.track("make-payment", this._paymentPayload);
    this._client.request(sessionIdQuery).then(function (_ref2) {
      var oneIncSessionId = _ref2.oneIncSessionId;

      _this._sessionId = oneIncSessionId;
      mixpanel.track("session-id-received", _this._paymentPayload);

      var oneIncPayload = {
        accountBalance: accountBalance,
        accountGroupCode: policyId.slice(0, 3),
        agencyLocationCode: agencyLocationCode,
        feeContext: "PaymentWithFee",
        insightPolicyId: insightPolicyId,
        minAmountDue: minAmountDue,
        paymentApplicationClient: paymentApplicationClient,
        referenceNumber: policyId,
        sessionId: _this._sessionId
      };

      var fullName = [firstName, lastName].filter(function (name) {
        return name && name.length > 0;
      }).join(" ");

      if (fullName.length > 0) {
        oneIncPayload.policyHolderName = fullName;
      }

      if (billingStreet) {
        oneIncPayload.billingAddressStreet = billingStreet;
      }

      if (billingZip) {
        oneIncPayload.billingZip = billingZip;
      }

      if (minCustomPaymentAmount) {
        oneIncPayload.minCustomPaymentAmount = minCustomPaymentAmount;
      }

      $("#" + PAYMENT_CONTAINER_ID).data("portalOne").makePayment(oneIncPayload);
    }).catch(function (err) {
      _this._eventbus.emit(PAYMENT_ERROR, err);
      mixpanel.track("session-id-error", _extends({
        error: (typeof err === "undefined" ? "undefined" : _typeof(err)) === "object" ? err.message : err
      }, _this._paymentPayload || {}));
    });
  };

  /**
   * Fetches session id then triggers save card modal
   * @param {object} payload
   * @param {string} payload.insightPolicyId
   * @param {string} payload.policyId
   * @memberof PaymentController
   */


  PaymentController.prototype.savePaymentMethod = function savePaymentMethod(payload) {
    var _this2 = this;

    this._paymentPayload = payload;
    this._initPortalOne(CARD_CONTAINER_ID, CARD_PLUGIN_NAME);

    mixpanel.track("save-payment-method", this._paymentPayload);
    this._client.request(savePaymentSessionIdQuery).then(function (_ref3) {
      var oneIncSavePaymentSessionId = _ref3.oneIncSavePaymentSessionId;

      _this2._sessionId = oneIncSavePaymentSessionId;
      mixpanel.track("save-credit-card-session-id-received", {
        insightPolicyId: payload.insightPolicyId,
        policyId: payload.policyId,
        type: payload.type
      });

      $("#" + CARD_CONTAINER_ID).data("portalOne").savePaymentMethod({
        paymentCategory: payload.type,
        referenceNumber: payload.policyId,
        sessionId: _this2._sessionId
      });
    });
  };

  /**
   * Listen to payment events, constants available:
   * PAYMENT_READY, PAYMENT_START, PAYMENT_CANCEL, PAYMENT_ERROR, PAYMENT_COMPLETE
   *
   * @param {string} eventName
   * @param {function} listener
   * @memberof PaymentController
   */


  PaymentController.prototype.addEventListener = function addEventListener(eventName, listener) {
    this._eventbus.on(eventName, listener);
  };

  /**
   * Unsubscribe from event
   *
   * @param {string} eventName
   * @param {function} listener
   * @memberof PaymentController
   */


  PaymentController.prototype.removeEventListener = function removeEventListener(eventName, listener) {
    if (listener) {
      this._eventbus.removeListener(eventName, listener);
    } else {
      this._eventbus.removeAllListeners(eventName);
    }
  };

  PaymentController.prototype.removeAllListeners = function removeAllListeners() {
    this._eventbus.removeAllListeners();
  };

  PaymentController.prototype._injectPortalOneScripts = function _injectPortalOneScripts(production, includeSavePayMethod) {
    var _this3 = this;

    var customSrc = production === true ? PORTALONE_SCRIPT_PROD : PORTALONE_SCRIPT_TEST;
    var genericSrc = production === true ? GENERIC_SCRIPT_PROD : GENERIC_SCRIPT_TEST;

    this._createScriptTag(PAYMENT_SCRIPT_ID, customSrc, function () {
      _this3._payment_ready = true;
      $.fn[PAYMENT_PLUGIN_NAME] = $.fn.portalOne;
    });

    if (includeSavePayMethod) {
      this._createScriptTag(CARD_SCRIPT_ID, genericSrc, function () {
        _this3._savecard_ready = true;
        $.fn[CARD_PLUGIN_NAME] = $.fn.portalOne;
      });
    }
  };

  PaymentController.prototype._createScriptTag = function _createScriptTag(scriptId, src, callback) {
    var _this4 = this;

    var script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = src;

      var prevOnLoad = script.onload || Function.prototype;
      script.onload = function () {
        prevOnLoad();
        script.setAttribute("data-loaded", "true");
        callback();
        if (_this4.isReady()) {
          setTimeout(function () {
            return _this4._eventbus.emit(PAYMENT_READY);
          }, 0);
        }
      };
      document.head.appendChild(script);
    }
  };

  PaymentController.prototype._initPortalOne = function _initPortalOne(containerId, pluginName) {
    var _this5 = this;

    // Remove any existing one inc. div and it's bound events
    $("#" + containerId).remove();
    // Add a new one inc div
    var $container = $("<div id=\"" + containerId + "\" />").appendTo("body");

    $container.on("portalOne.load", function () {
      _this5._eventbus.emit(PAYMENT_START);
      mixpanel.track(PAYMENT_START, _this5._paymentPayload);
    });
    $container.on("portalOne.error", function () {
      _this5._eventbus.emit(PAYMENT_ERROR, new Error("Payment processor error"));
      mixpanel.track(PAYMENT_ERROR, _this5._paymentPayload);
    });
    $container.on("portalOne.paymentCanceled", function () {
      _this5._eventbus.emit(PAYMENT_CANCEL);
      mixpanel.track(PAYMENT_CANCEL, _this5._paymentPayload);
    });
    $container.on("portalOne.paymentComplete", function (e, res) {
      _this5._handlePayment(res);
      mixpanel.track("oneinc-complete", _this5._paymentPayload);
    });
    $container.on("portalOne.saveCanceled", function () {
      _this5._eventbus.emit(SAVE_CC_CANCEL);
      mixpanel.track(SAVE_CC_CANCEL, _this5._paymentPayload);
    });
    $container.on("portalOne.saveComplete", function (e, res) {
      if (_this5._paymentPayload.type === SAVE_PAYMENT_CC) _this5._handleSaveCreditCardInfo(res);else _this5._handleSaveACHInfo(res);
      mixpanel.track("oneinc-save-credit-card-payment-complete", res);
    });

    mixpanel.track("container-loaded", this._paymentPayload);

    if (typeof $container[pluginName] !== "function") {
      mixpanel.track("missing-plugin", _extends({}, this._paymentPayload || {}, {
        pluginName: pluginName,
        jQueryVersion: $.fn.jquery
      }));
    }
    $container[pluginName]();
  };

  PaymentController.prototype._handlePayment = function _handlePayment(_ref4) {
    var _this6 = this;

    var bankName = _ref4.bankName,
        insightPolicyId = _ref4.insightPolicyId,
        lastFourDigits = _ref4.lastFourDigits,
        paymentAmount = _ref4.paymentAmount,
        paymentApplicationClient = _ref4.paymentApplicationClient,
        paymentCategory = _ref4.paymentCategory,
        transactionDate = _ref4.transactionDate,
        transactionId = _ref4.transactionId;

    if (!this._paymentPayload) {
      mixpanel.track(PAYMENT_NO_PAYLOAD);
      return;
    }

    var info = {
      accountNumber: lastFourDigits,
      amount: paymentAmount,
      bankName: bankName,
      billingAddressCity: this._paymentPayload.billingCity,
      billingAddressLine1: this._paymentPayload.billingStreet,
      billingAddressState: this._paymentPayload.billingState,
      billingAddressZipCode: this._paymentPayload.billingZip,
      clientId: paymentApplicationClient,
      insightPolicyId: insightPolicyId,
      insuredEmailAddress: this._paymentPayload.email,
      insuredPhoneNumber: this._paymentPayload.phone,
      method: paymentMethod(paymentCategory),
      payorFirstName: this._paymentPayload.firstName,
      payorLastName: this._paymentPayload.lastName,
      policyId: this._paymentPayload.policyId,
      processedDateTime: transactionDate,
      quoteId: this._paymentPayload.quoteId,
      success: true,
      verificationNumber: transactionId
    };

    retryGraphQL({
      client: this._client,
      query: paymentMutation,
      variables: { payment: info },
      times: 3
    }).then(function (res) {
      _this6._eventbus.emit(PAYMENT_COMPLETE, res);
      mixpanel.track(PAYMENT_COMPLETE, _extends({}, _this6._paymentPayload || {}, info));
      _this6._paymentPayload = null;
    }).catch(function (err) {
      _this6._eventbus.emit(PAYMENT_COMPLETE_ERROR, err);
      mixpanel.track(PAYMENT_ERROR, _extends({}, _this6._paymentPayload || {}, info));
      _this6._paymentPayload = null;
    });
  };

  PaymentController.prototype._handleSaveACHInfo = function _handleSaveACHInfo(savedData) {
    var _this7 = this;

    if (!this._paymentPayload) return;
    var method = {
      accountType: savedData.accountType,
      bankName: savedData.bankName,
      customerName: savedData.customerName,
      insightPolicyId: this._paymentPayload.insightPolicyId,
      lastFourDigits: savedData.lastFourDigits,
      sessionId: this._sessionId,
      timezone: savedData.timeZone,
      tokenId: savedData.tokenId,
      transactionDate: savedData.transactionDate
    };

    retryGraphQL({
      client: this._client,
      query: saveACHMethodMutation,
      variables: { method: method },
      times: 3
    }).then(function (res) {
      _this7._eventbus.emit(SAVE_CC_COMPLETE, res);
      mixpanel.track(SAVE_CC_COMPLETE, res);
      _this7._paymentPayload = null;
    }).catch(function (err) {
      _this7._eventbus.emit(SAVE_CC_ERROR, err);
      mixpanel.track(SAVE_CC_ERROR, method);
      _this7._paymentPayload = null;
    });
  };

  PaymentController.prototype._handleSaveCreditCardInfo = function _handleSaveCreditCardInfo(savedData) {
    var _this8 = this;

    if (!this._paymentPayload) return;
    var cardExpirationMonth = savedData.cardExpirationMonth,
        cardExpirationYear = savedData.cardExpirationYear,
        cardType = savedData.cardType,
        customerName = savedData.customerName,
        holderZip = savedData.holderZip,
        lastFourDigits = savedData.lastFourDigits,
        sessionId = savedData.sessionId,
        timeZone = savedData.timeZone,
        tokenId = savedData.tokenId,
        transactionDate = savedData.transactionDate;


    var method = {
      cardExpirationMonth: cardExpirationMonth,
      cardExpirationYear: cardExpirationYear,
      cardHolderZip: holderZip,
      cardType: cardType,
      customerName: customerName,
      insightPolicyId: this._paymentPayload.insightPolicyId,
      lastFourDigits: lastFourDigits,
      sessionId: sessionId,
      timezone: timeZone,
      tokenId: tokenId,
      transactionDate: transactionDate
    };

    retryGraphQL({
      client: this._client,
      query: saveCreditCardMethodMutation,
      variables: { method: method },
      times: 3
    }).then(function (res) {
      _this8._eventbus.emit(SAVE_CC_COMPLETE, res);
      mixpanel.track(SAVE_CC_COMPLETE, res);
      _this8._paymentPayload = null;
    }).catch(function (err) {
      _this8._eventbus.emit(SAVE_CC_ERROR, err);
      mixpanel.track(SAVE_CC_ERROR, method);
      _this8._paymentPayload = null;
    });
  };

  return PaymentController;
}(), _class.PAYMENT_READY = PAYMENT_READY, _class.PAYMENT_START = PAYMENT_START, _class.PAYMENT_CANCEL = PAYMENT_CANCEL, _class.PAYMENT_ERROR = PAYMENT_ERROR, _class.PAYMENT_COMPLETE = PAYMENT_COMPLETE, _class.SAVE_CC_CANCEL = SAVE_CC_CANCEL, _class.SAVE_CC_COMPLETE = SAVE_CC_COMPLETE, _class.SAVE_CC_ERROR = SAVE_CC_ERROR, _temp);
export { PaymentController as default };


function validate(_ref5) {
  var accountBalance = _ref5.accountBalance,
      agencyLocationCode = _ref5.agencyLocationCode,
      billingCity = _ref5.billingCity,
      billingState = _ref5.billingState,
      billingStreet = _ref5.billingStreet,
      billingZip = _ref5.billingZip,
      email = _ref5.email,
      firstName = _ref5.firstName,
      insightPolicyId = _ref5.insightPolicyId,
      lastName = _ref5.lastName,
      minAmountDue = _ref5.minAmountDue,
      minCustomPaymentAmount = _ref5.minCustomPaymentAmount,
      paymentApplicationClient = _ref5.paymentApplicationClient,
      phone = _ref5.phone,
      policyId = _ref5.policyId,
      quoteId = _ref5.quoteId;

  var requiredStrings = [email, paymentApplicationClient, phone, policyId, quoteId].every(function (val) {
    return isDefined(val) && isString(val);
  });

  var requireNumbers = [accountBalance, minAmountDue].every(function (val) {
    return isDefined(val) && isNumber(val);
  });

  var optionalStrings = [agencyLocationCode, billingCity, billingState, billingStreet, billingZip, firstName, insightPolicyId, lastName].filter(isDefined).every(function (val) {
    return isString(val);
  });

  var optionalNumbers = [minCustomPaymentAmount].filter(isDefined).every(function (val) {
    return isNumber(val);
  });

  return requiredStrings && requireNumbers && optionalStrings && optionalNumbers;
}

function isDefined(val) {
  return typeof val !== "undefined";
}

function isNumber(val) {
  return typeof val === "number";
}

function isString(val) {
  return typeof val === "string";
}

function paymentMethod(val) {
  return val && val.toLowerCase() === "creditcard" ? "CREDITCARD" : "ACH";
}

function retryGraphQL(_ref6) {
  var client = _ref6.client,
      query = _ref6.query,
      variables = _ref6.variables,
      times = _ref6.times;

  function clientReq(resolve, reject) {
    var attempt = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
    var delay = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 400;

    client.request(query, variables).then(function (res) {
      return resolve(res);
    }).catch(function (err) {
      var isServerError = err && err.response && err.response.status === 200;
      if (isServerError || attempt === times) {
        reject(err);
        return;
      }
      mixpanel.track("retry-payment-notice", variables.payment);
      setTimeout(function () {
        return clientReq(resolve, reject, attempt + 1, delay * 3);
      }, delay);
    });
  }

  return new Promise(clientReq);
}